import get from 'lodash/get';
import * as React from 'react';

import { useVoiceboxSettings } from 'bones/components/molecules/Voicebox/context/VoiceboxSettings';
import {
  VoiceboxResponseActionType,
  VoiceboxResponseMessage,
} from 'bones/constants/voicebox/message';
import {
  ColumnMetadata,
  DataSource,
  LocalGraph,
  QueryProvenance,
  SchemaMetadata,
  TableMetadata,
  VirtualGraph,
} from 'bones/constants/voicebox/provenance';
import { VoiceboxSettings } from 'bones/constants/voicebox/settings';
import { getLocalName } from 'bones/utils/getLocalName';
import { useLocalGraphsFilter } from 'bones/utils/voicebox/useLocalGraphsFilter';

export const useProvenanceFromMessage = (
  message: VoiceboxResponseMessage | null,
  settings: VoiceboxSettings | null
): (QueryProvenance & { types: string[] }) | null => {
  const { databasesById, namedGraphsByDatabaseId } = useVoiceboxSettings();

  const filteredGraphs = useLocalGraphsFilter(
    settings?.databaseId || '',
    databasesById,
    settings?.namedGraphs || [],
    namedGraphsByDatabaseId
  );

  return React.useMemo(() => {
    const actions = message?.actions?.filter(
      (action) => action.type === VoiceboxResponseActionType.PROVENANCE
    );
    if (!actions || !actions.length) {
      return null;
    }

    const dataSourcesByIri: Record<string, DataSource> = {};
    const schemasByIri: Record<string, SchemaMetadata> = {};
    const tablesByIri: Record<string, TableMetadata> = {};
    const columnsByIri: Record<string, ColumnMetadata> = {};
    const graphsByIri: Record<string, LocalGraph | VirtualGraph> = {};
    const types = new Set<string>();

    const addMetadataFromData =
      (data: any) =>
      <T, K extends keyof T>(
        metadata: Pick<{ [key in keyof T]: T[K] }, K>,
        metadataKey: K,
        ...path: string[]
      ) => {
        const value = get(data, path.length ? path : metadataKey, null);
        if (value) {
          // eslint-disable-next-line no-param-reassign
          metadata[metadataKey] = value;
        }
      };

    actions.forEach((action) => {
      try {
        const maybeData = JSON.parse(action.value);

        Object.entries(maybeData?.metadata?.data_sources || {}).forEach(
          ([iri, data]) => {
            const addMetadata = addMetadataFromData(data);

            // istanbul ignore else
            if (!dataSourcesByIri[iri]) {
              dataSourcesByIri[iri] = {
                iri,
                platform: '',
                name: getLocalName(iri),
              };
            }

            const dataSource = dataSourcesByIri[iri];
            addMetadata(dataSource, 'platform');
            addMetadata(dataSource, 'name');
            addMetadata(dataSource, 'description');
          }
        );

        Object.entries(maybeData?.metadata?.schemas || {}).forEach(
          ([iri, data]) => {
            const addMetadata = addMetadataFromData(data);

            // istanbul ignore else
            if (!schemasByIri[iri]) {
              schemasByIri[iri] = {
                iri,
                name: getLocalName(iri),
              };
            }

            const schema = schemasByIri[iri];
            addMetadata(schema, 'name');
          }
        );

        Object.entries(maybeData?.metadata?.tables || {}).forEach(
          ([iri, data]) => {
            const addMetadata = addMetadataFromData(data);

            // istanbul ignore else
            if (!tablesByIri[iri]) {
              tablesByIri[iri] = {
                iri,
                name: getLocalName(iri),
              };
            }

            const table = tablesByIri[iri];
            addMetadata(table, 'name');
            addMetadata(table, 'schemaIri', 'schema_iri');
          }
        );

        Object.entries(maybeData?.metadata?.columns || {}).forEach(
          ([iri, data]) => {
            const addMetadata = addMetadataFromData(data);

            // istanbul ignore else
            if (!columnsByIri[iri]) {
              columnsByIri[iri] = {
                iri,
                name: getLocalName(iri),
                tableIri: '',
              };
            }

            const column = columnsByIri[iri];
            addMetadata(column, 'name');
            addMetadata(column, 'tableIri', 'table_iri');
          }
        );

        maybeData?.graphs?.forEach((data: any) => {
          const addMetadata = addMetadataFromData(data);

          if (data?.types) {
            if (!filteredGraphs.has(data.iri)) {
              return;
            }

            if (!graphsByIri[data.iri]) {
              graphsByIri[data.iri] = {
                iri: data.iri,
                name: getLocalName(data.iri),
                types: [],
              };
            }

            const graph = graphsByIri[data.iri] as LocalGraph;
            addMetadata(graph, 'name');
            addMetadata(graph, 'description');
            data.types.forEach((type: string) => {
              if (type && !graph.types.includes(type)) {
                types.add(type);
                graph.types.push(type);
              }
            });
          }

          if (data?.mappings) {
            if (!graphsByIri[data.iri]) {
              graphsByIri[data.iri] = {
                iri: data.iri,
                name: getLocalName(data.iri),
                database: '',
                dataSourceIri: '',
                mappings: [],
              };
            }

            const graph = graphsByIri[data.iri] as VirtualGraph;
            addMetadata(graph, 'name');
            addMetadata(graph, 'description');
            addMetadata(graph, 'dataSourceIri', 'datasource_iri');
            addMetadata(graph, 'database');
            data.mappings.forEach((mappingData: any) => {
              types.add(mappingData.to_type);
              graph.mappings.push({
                iri: mappingData.iri,
                toType: mappingData.to_type,
                typeMappings: mappingData.type_mappings.map(
                  (typeData: any) => ({
                    columnIri: typeData.column_iri,
                    property: typeData.property,
                    datatype: typeData.datatype,
                  })
                ),
                propertyMappings: mappingData.property_mappings.map(
                  (propertyData: any) => ({
                    columnIri: propertyData.column_iri,
                    property: propertyData.property,
                    datatype: propertyData.datatype,
                  })
                ),
              });
            });
          }
        });
      } catch (err) {
        console.warn('Problem loading provenance', err);
      }
    });

    return {
      graphs: Object.values(graphsByIri),
      metadata: {
        dataSourcesByIri,
        schemasByIri,
        tablesByIri,
        columnsByIri,
      },
      types: Array.from(types),
    };
  }, [filteredGraphs, message]);
};
